import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ConnectionStatus from "components/ui/ConnectionStatus";
import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';
import NotFound from "pages/NotFound";
import IndividualPropertyPage from './pages/individual-property-page';
import LegalServicesCenter from './pages/legal-services-center';
import PropertySearchListings from './pages/property-search-listings';
import AboutTrustCenter from './pages/about-trust-center';
import CommuneExplorer from './pages/commune-explorer';
import Homepage from './pages/homepage';
import UserAppointmentManagement from './pages/user-appointment-management';
import PropertyManagementDashboard from './pages/property-management-dashboard';
import PropertyCreationManagementDashboard from './pages/property-creation-management-dashboard';
import FrontendContentManagementSystem from './pages/frontend-content-management-system';
import UniversalContentManagementSystem from './pages/universal-content-management-system';
import AdminDashboard from './pages/admin-dashboard';
import CommuneManagementDashboard from './pages/commune-management-dashboard';
import AdvancedPropertyManagementInterface from './pages/advanced-property-management-interface';
import IntegratedDataManagementCenter from './pages/integrated-data-management-center';
import FeaturedPropertiesManagementDashboard from './pages/featured-properties-management-dashboard';
import NotaryAppointmentBookingSystem from './pages/notary-appointment-booking-system';
import NotaryManagementDashboard from './pages/notary-management-dashboard';
import VideoTourDiscoveryCenter from './pages/video-tour-discovery-center';
import ConsultationBookingHub from './pages/consultation-booking-hub';
import LegalComplianceFooter from './pages/legal-compliance-footer';
import ROICalculatorTool from './pages/roi-calculator-tool';
import MarketAnalysisDashboard from './pages/market-analysis-dashboard';
import AssetManagementHub from './pages/asset-management-hub';
import ImmersivePropertyTour from './pages/immersive-property-tour';
import ModelManagementDashboard from './pages/model-management-dashboard';
import NotificationHistory from './pages/notification-history';
import NotificationPreferences from './pages/notification-preferences';
import NotificationsCenter from './pages/notifications-center';
import NotificationHub from './pages/notification-hub';
import AccountDashboard from './pages/account-dashboard';
import ReschedulePage from 'pages/reschedule';
import CommunicationSettings from './pages/account-communication-settings';
import AuthPage from './pages/auth';
import UserProfilePage from './pages/user-profile';
import ActivityHistoryPage from './pages/activity-history';
import ActivityDigestPage from './pages/activity-digest';
import KnowledgeBase from './pages/knowledge-base';
import AlertPreferencesPanel from './pages/alert-preferences';
import ProfessionalServicesBooking from './pages/professional-services-booking';
import InAppMessaging from './pages/in-app-messaging';
import ProfessionalSignupPage from './pages/professional-signup';
import ProfessionalEmailVerification from './pages/professional-email-verification';
import InvestorMode from './pages/investor-mode';
import InvestorWatchlist from './pages/investor-watchlist';
import ProfessionalDashboard from './pages/professional-dashboard';
import UserProfileManagement from './pages/user-profile-management';
import UserEmailSettings from './pages/user-email-settings';
import ProfessionalPortal from './pages/professional-portal';
import ProfessionalInbox from './pages/professional-inbox';
import ProfessionalOnboarding from './pages/professional-onboarding';
import AdminApplicationsDashboard from './pages/admin-applications-dashboard';
import MonEspace from './pages/mon-espace';
import DemarchesAdministratives from './pages/demarches-administratives';
import NotaireLoginPage from './pages/notaire-login';
import NotaireDashboard from './pages/notaire-dashboard';
import NotaryPerformancePage from './pages/notary-performance';
import KPSCreatorStudio from './pages/kps-creator-studio';
import PaymentSuccess from './pages/payment-success';
import PaymentFailure from './pages/payment-failure';
import AgentMobileDashboard from './pages/agent-mobile-dashboard';
import CredentialSubmissionWorkflow from './pages/credential-submission-workflow';
import AgentPayoutVerification from './pages/agent-payout-verification';
import EscrowDisputeEscalation from './pages/escrow-dispute-escalation';
import EscrowSettlementDashboard from './pages/escrow-settlement-dashboard';
import KpsPayCheckout from './pages/KpsPayCheckout';

// Inner component so the hook has access to BrowserRouter context
const GATracker = () => {
  useGoogleAnalytics();
  return null;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <GATracker />
        <ScrollToTop />
        <ConnectionStatus />
        <RouterRoutes>
          {/* Main routes */}
          <Route path="/" element={<Homepage />} />
          <Route path="/homepage" element={<Homepage />} />
          <Route path="/about-trust-center" element={<AboutTrustCenter />} />
          <Route path="/legal-compliance-footer" element={<LegalComplianceFooter />} />
          
          {/* Property routes */}
          <Route path="/individual-property-page" element={<IndividualPropertyPage />} />
          <Route path="/individual-property-page/:propertyId" element={<IndividualPropertyPage />} />
          <Route path="/property/:propertyId" element={<IndividualPropertyPage />} />
          <Route path="/properties/:propertyId" element={<IndividualPropertyPage />} />
          <Route path="/property-search-listings" element={<PropertySearchListings />} />
          <Route path="/properties" element={<PropertySearchListings />} />
          <Route path="/property-management-dashboard" element={<PropertyManagementDashboard />} />
          <Route path="/property-creation-management-dashboard" element={<PropertyCreationManagementDashboard />} />
          <Route path="/advanced-property-management-interface" element={<AdvancedPropertyManagementInterface />} />
          <Route path="/featured-properties-management-dashboard" element={<FeaturedPropertiesManagementDashboard />} />
          
          {/* Financial Analysis routes */}
          <Route path="/roi-calculator-tool" element={<ROICalculatorTool />} />
          <Route path="/roi-calculator" element={<ROICalculatorTool />} />
          <Route path="/calculateur-roi" element={<ROICalculatorTool />} />
          <Route path="/market-analysis-dashboard" element={<MarketAnalysisDashboard />} />
          <Route path="/market-analysis" element={<MarketAnalysisDashboard />} />
          <Route path="/analyse-marche" element={<MarketAnalysisDashboard />} />
          
          {/* Commune routes */}
          <Route path="/commune-explorer" element={<CommuneExplorer />} />
          <Route path="/commune-explorer/:communeId" element={<CommuneExplorer />} />
          <Route path="/commune/:communeId" element={<CommuneExplorer />} />
          <Route path="/communes/:communeId" element={<CommuneExplorer />} />
          <Route path="/commune-management-dashboard" element={<CommuneManagementDashboard />} />
          
          {/* Legal and services routes */}
          <Route path="/legal-services-center" element={<LegalServicesCenter />} />
          <Route path="/legal-services" element={<LegalServicesCenter />} />
          <Route path="/notary-appointment-booking-system" element={<NotaryAppointmentBookingSystem />} />
          <Route path="/notary-booking" element={<NotaryAppointmentBookingSystem />} />
          <Route path="/notary-management-dashboard" element={<NotaryManagementDashboard />} />
          <Route path="/consultation-booking-hub" element={<ConsultationBookingHub />} />
          <Route path="/consultation" element={<ConsultationBookingHub />} />
          
          {/* Management routes */}
          <Route path="/user-appointment-management" element={<UserAppointmentManagement />} />
          <Route path="/appointments" element={<UserAppointmentManagement />} />
          <Route path="/admin-dashboard" element={<AdminDashboard />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/integrated-data-management-center" element={<IntegratedDataManagementCenter />} />
          <Route path="/data-management" element={<IntegratedDataManagementCenter />} />
          
          {/* Content management routes */}
          <Route path="/frontend-content-management-system" element={<FrontendContentManagementSystem />} />
          <Route path="/content-management" element={<FrontendContentManagementSystem />} />
          <Route path="/universal-content-management-system" element={<UniversalContentManagementSystem />} />
          
          {/* Media routes */}
          <Route path="/video-tour-discovery-center" element={<VideoTourDiscoveryCenter />} />
          <Route path="/video-tours" element={<VideoTourDiscoveryCenter />} />
          <Route path="/videos" element={<VideoTourDiscoveryCenter />} />
          <Route path="/asset-management-hub" element={<AssetManagementHub />} />
          <Route path="/3d-assets" element={<AssetManagementHub />} />
          <Route path="/model-management-dashboard" element={<ModelManagementDashboard />} />
          <Route path="/3d-models-admin" element={<ModelManagementDashboard />} />
          
          {/* Notification History */}
          <Route path="/notification-history" element={<NotificationHistory />} />
          <Route path="/notifications" element={<NotificationsCenter />} />
          <Route path="/notifications-center" element={<NotificationsCenter />} />
          <Route path="/notification-hub" element={<NotificationHub />} />
          <Route path="/hub-notifications" element={<NotificationHub />} />
          <Route path="/notification-preferences" element={<NotificationPreferences />} />
          <Route path="/preferences-notifications" element={<NotificationPreferences />} />
          <Route path="/alert-preferences" element={<AlertPreferencesPanel />} />
          <Route path="/preferences-alertes" element={<AlertPreferencesPanel />} />
          <Route path="/professional-services-booking" element={<ProfessionalServicesBooking />} />
          <Route path="/services-professionnels" element={<ProfessionalServicesBooking />} />
          <Route path="/in-app-messaging" element={<InAppMessaging />} />
          <Route path="/messagerie" element={<InAppMessaging />} />
          <Route path="/professional-signup" element={<ProfessionalSignupPage />} />
          <Route path="/devenir-prestataire" element={<ProfessionalSignupPage />} />
          <Route path="/professional-email-verification" element={<ProfessionalEmailVerification />} />
          <Route path="/investor-mode" element={<InvestorMode />} />
          <Route path="/mode-investisseur" element={<InvestorMode />} />
          <Route path="/investor-watchlist" element={<InvestorWatchlist />} />
          <Route path="/professional-dashboard" element={<ProfessionalDashboard />} />
          <Route path="/tableau-de-bord-pro" element={<ProfessionalDashboard />} />
          <Route path="/professional-portal" element={<ProfessionalPortal />} />
          <Route path="/espace-professionnel" element={<ProfessionalPortal />} />
          <Route path="/professional-inbox" element={<ProfessionalInbox />} />
          <Route path="/boite-reception-pro" element={<ProfessionalInbox />} />
          <Route path="/professional-onboarding" element={<ProfessionalOnboarding />} />
          <Route path="/onboarding-professionnel" element={<ProfessionalOnboarding />} />
          <Route path="/admin-applications-dashboard" element={<AdminApplicationsDashboard />} />
          <Route path="/admin/candidatures" element={<AdminApplicationsDashboard />} />

          {/* Account Dashboard */}
          <Route path="/account-dashboard" element={<AccountDashboard />} />
          <Route path="/mon-compte" element={<AccountDashboard />} />
          <Route path="/account-communication-settings" element={<CommunicationSettings />} />
          <Route path="/parametres-communication" element={<CommunicationSettings />} />

          {/* Mon Espace */}
          <Route path="/mon-espace" element={<MonEspace />} />
          <Route path="/my-space" element={<MonEspace />} />

          {/* Démarches Administratives */}
          <Route path="/demarches-administratives" element={<DemarchesAdministratives />} />
          <Route path="/demarches" element={<DemarchesAdministratives />} />

          {/* Auth & Profile */}
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/login" element={<AuthPage />} />
          <Route path="/signup" element={<AuthPage />} />
          <Route path="/user-profile" element={<UserProfilePage />} />
          <Route path="/mon-profil" element={<UserProfilePage />} />
          <Route path="/user-profile-management" element={<UserProfileManagement />} />
          <Route path="/gestion-profil" element={<UserProfileManagement />} />
          <Route path="/user-email-settings" element={<UserEmailSettings />} />
          <Route path="/parametres-email" element={<UserEmailSettings />} />
          <Route path="/activity-history" element={<ActivityHistoryPage />} />
          <Route path="/historique-activite" element={<ActivityHistoryPage />} />
          <Route path="/activity-digest" element={<ActivityDigestPage />} />
          <Route path="/digest-activite" element={<ActivityDigestPage />} />
          <Route path="/knowledge-base" element={<KnowledgeBase />} />
          <Route path="/centre-aide" element={<KnowledgeBase />} />

          {/* Notaire Portal */}
          <Route path="/notaire/login" element={<NotaireLoginPage />} />
          <Route path="/notaire/dashboard" element={<NotaireDashboard />} />
          <Route path="/espace-notaire" element={<NotaireLoginPage />} />
          <Route path="/notaire/performance/:notaryId" element={<NotaryPerformancePage />} />
          <Route path="/notaire/performance" element={<NotaryPerformancePage />} />

          {/* KPS Creator Studio Download */}
          <Route path="/kps-creator-studio" element={<KPSCreatorStudio />} />
          <Route path="/telecharger" element={<KPSCreatorStudio />} />

          {/* Payment landing pages */}
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/paiement-confirme" element={<PaymentSuccess />} />
          <Route path="/payment-failure" element={<PaymentFailure />} />
          <Route path="/paiement-echoue" element={<PaymentFailure />} />
          <Route path="/kpspay-checkout" element={<KpsPayCheckout />} />

          {/* Agent Mobile Dashboard */}
          <Route path="/agent-mobile-dashboard" element={<AgentMobileDashboard />} />
          <Route path="/dashboard-agent" element={<AgentMobileDashboard />} />

          {/* Agent Payout Verification */}
          <Route path="/agent-payout-verification" element={<AgentPayoutVerification />} />
          <Route path="/verification-paiements" element={<AgentPayoutVerification />} />

          {/* Credential Submission Workflow */}
          <Route path="/credential-submission" element={<CredentialSubmissionWorkflow />} />
          <Route path="/soumettre-credentials" element={<CredentialSubmissionWorkflow />} />

          {/* Escrow Dispute Escalation */}
          <Route path="/escrow-dispute-escalation" element={<EscrowDisputeEscalation />} />
          <Route path="/escalade-litige-sequestre" element={<EscrowDisputeEscalation />} />

          {/* Escrow Settlement Dashboard */}
          <Route path="/escrow-settlement-dashboard" element={<EscrowSettlementDashboard />} />
          <Route path="/reglement-sequestre" element={<EscrowSettlementDashboard />} />

          {/* Immersive 3D Tour routes */}
          <Route path="/visite-3d/:propertyId" element={<ImmersivePropertyTour />} />
          <Route path="/immersive-tour/:propertyId" element={<ImmersivePropertyTour />} />
          <Route path="/tour-3d/:propertyId" element={<ImmersivePropertyTour />} />
          <Route path="/reschedule" element={<ReschedulePage />} />
          
          {/* Error routes */}
          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;